import React, { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { formatMoneyFromMinor, normalizeSketchplanCurrency, SKETCHPLAN_PRICING_COPY } from '@rehub/pricing';
import { supabase } from '../services/supabase';

interface OrderWorkspaceProps {
  order: {
    id: string;
    sketch_url?: string | null;
    notes?: string | null;
    payment_method?: string | null;
    payment_mode?: string | null;
    amount_pence?: number | null;
    currency?: string | null;
    quoted_price?: number | null;
    job_number?: string | null;
    amendment_used_at?: string | null;
    draft_url?: string | null;
    status: string;
  };
}

const OrderWorkspace: React.FC<OrderWorkspaceProps> = ({ order }) => {
  const [messages, setMessages] = useState<{ id: string; author_role: string; kind: string; body: string; created_at: string }[]>([]);
  const [body, setBody] = useState('');
  const [amendment, setAmendment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('pro_order_messages')
      .select('id, author_role, kind, body, created_at')
      .eq('order_id', order.id)
      .order('created_at', { ascending: true })
      .then(({ data }) => setMessages(data || []));
  }, [order.id]);

  const sendMessage = async () => {
    if (!body.trim()) return;
    setSending(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error: insertError } = await supabase.from('pro_order_messages').insert({
      order_id: order.id,
      author_id: auth.user?.id ?? null,
      author_role: 'customer',
      kind: 'message',
      body: body.trim(),
    });
    setSending(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    setBody('');
    const { data } = await supabase
      .from('pro_order_messages')
      .select('id, author_role, kind, body, created_at')
      .eq('order_id', order.id)
      .order('created_at', { ascending: true });
    setMessages(data || []);
  };

  const sendAmendment = async () => {
    setError(null);
    setSending(true);
    const { data, error: rpcError } = await supabase.rpc('request_pro_order_amendment', {
      p_order_id: order.id,
      p_body: amendment.trim(),
    });
    setSending(false);
    if (rpcError || data?.success === false) {
      setError(data?.error === 'AMENDMENT_ALREADY_USED' ? 'Your included amendment has already been used.' : 'Could not send the amendment.');
      return;
    }
    setAmendment('');
    const { data: rows } = await supabase
      .from('pro_order_messages')
      .select('id, author_role, kind, body, created_at')
      .eq('order_id', order.id)
      .order('created_at', { ascending: true });
    setMessages(rows || []);
  };

  const paidLabel = order.amount_pence != null
    ? formatMoneyFromMinor(order.amount_pence, normalizeSketchplanCurrency(order.currency))
    : order.quoted_price != null
      ? `${order.quoted_price}`
      : '—';

  return (
    <div className="mt-4 space-y-4 border-t border-slate-800 pt-4">
      <div className="grid gap-3 sm:grid-cols-2 text-sm">
        <div className="rounded-xl bg-slate-800/50 border border-slate-700 px-3 py-2">
          <p className="text-xs text-slate-500">Payment</p>
          <p className="text-slate-200">
            {order.payment_mode || order.payment_method || 'credits'} · {paidLabel}
            {order.job_number ? ` · ${order.job_number}` : ''}
          </p>
        </div>
        {order.draft_url && (
          <a href={order.draft_url} target="_blank" rel="noreferrer" className="rounded-xl bg-slate-800/50 border border-slate-700 px-3 py-2 text-teal-300 hover:text-teal-200">
            Download draft
          </a>
        )}
      </div>
      {order.notes && <p className="text-xs text-slate-500">{order.notes}</p>}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Project messages</p>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {messages.length === 0 && <p className="text-xs text-slate-500">No messages yet.</p>}
          {messages.map((msg) => (
            <div key={msg.id} className="rounded-lg bg-slate-800/40 px-3 py-2 text-sm text-slate-300">
              <span className="text-[10px] uppercase text-slate-500">{msg.author_role} · {msg.kind}</span>
              <p>{msg.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <input value={body} onChange={(e) => setBody(e.target.value)} className="flex-1 rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-white" placeholder="Message the production team" />
          <button type="button" onClick={sendMessage} disabled={sending} className="px-3 rounded-lg bg-slate-700 text-white">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Included amendment</p>
        <p className="text-xs text-slate-500 mb-2">{SKETCHPLAN_PRICING_COPY.amendmentLong}</p>
        {order.amendment_used_at ? (
          <p className="text-xs text-slate-500">
            Your included amendment round has been submitted. If the plan still doesn&apos;t match the sketch you originally supplied, message the production team and we&apos;ll correct it at no charge.
          </p>
        ) : (
          <div className="flex gap-2">
            <input value={amendment} onChange={(e) => setAmendment(e.target.value)} className="flex-1 rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-white" placeholder="Describe the change" />
            <button type="button" onClick={sendAmendment} disabled={sending || !amendment.trim()} className="px-3 rounded-lg bg-teal-700 text-white text-sm">
              Submit
            </button>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};

export default OrderWorkspace;
